import posthog from "posthog-js";

const POSTHOG_CONFIG = {
  apiKey: "phc_CT6tqmevxJdAWPt2R2YfaYBzKoX0OxkvFdVLXx3ZIal",
  host: "https://posthog.aisensy.org/",
  // Enable debug mode in development
  debug: process.env.REACT_APP_MODE !== "production",
  // Track pageviews automatically
  capture_pageview: true,
  // Capture performance metrics
  capture_performance: true,
  // Enable session recording
  session_recording: {
    enabled: true,
    recordCrossOriginIframes: true,
  },
  // Advanced features
  autocapture: true,
  capture_pageleave: true,
  // Disable in development if needed
  disabled: false,
};

// Safe PostHog wrapper with error handling
class SafePostHog {
  constructor() {
    this.isInitialized = false;
    this.isEnabled = true;
    this.failedAttempts = 0;
    this.maxFailedAttempts = 3;
  }

  init() {
    try {
      if (typeof window === "undefined") return;

      // Set a timeout to prevent blocking
      const initTimeout = setTimeout(() => {
        console.warn("PostHog initialization timeout, disabling");
        this.disable();
      }, 3000); // 3 second timeout

      posthog.init(POSTHOG_CONFIG.apiKey, {
        api_host: POSTHOG_CONFIG.host,
        debug: POSTHOG_CONFIG.debug,
        capture_pageview: POSTHOG_CONFIG.capture_pageview,
        capture_performance: POSTHOG_CONFIG.capture_performance,
        session_recording: POSTHOG_CONFIG.session_recording,
        autocapture: POSTHOG_CONFIG.autocapture,
        capture_pageleave: POSTHOG_CONFIG.capture_pageleave,
        disabled: POSTHOG_CONFIG.disabled,
        // Disable feature flags to prevent API failures
        bootstrap: {},
        disable_feature_flags: true,
        // Disable other potentially problematic features
        disable_surveys: true,
        disable_toolbar: true,
        disable_compression: false,
        // Prevent automatic API calls that might fail
        loaded: (posthog) => {
          // Disable feature flags after load
          posthog.featureFlags = {};
        },
        // Add timeout and retry settings
        request_timeout_ms: 5000, // 5 second timeout for requests
        on_xhr_error: (failedRequest) => {
          console.warn("PostHog request failed:", failedRequest);
          this.failedAttempts++;
          if (this.failedAttempts >= this.maxFailedAttempts) {
            this.disable();
          }
        },
        // Disable features that might cause issues
        disable_session_recording: false,
        disable_persistence: false,
        // Add retry logic
        retry_queue_size: 3,
        retry_delay_ms: 1000,
      });

      clearTimeout(initTimeout);
      this.isInitialized = true;

      // Override PostHog methods to prevent feature flag API calls
      this.overridePostHogMethods();

      console.log("PostHog initialized successfully");
    } catch (error) {
      console.warn("PostHog initialization failed:", error);
      this.disable();
    }
  }

  capture(event, properties = {}) {
    if (!this.isEnabled || !this.isInitialized) return;

    try {
      // Use setTimeout to make analytics non-blocking
      setTimeout(() => {
        try {
          posthog.capture(event, properties);
        } catch (error) {
          console.warn("PostHog capture failed:", error);
          this.failedAttempts++;
          if (this.failedAttempts >= this.maxFailedAttempts) {
            this.disable();
          }
        }
      }, 0);
    } catch (error) {
      console.warn("PostHog capture setup failed:", error);
      this.failedAttempts++;
      if (this.failedAttempts >= this.maxFailedAttempts) {
        this.disable();
      }
    }
  }

  identify(userId, properties = {}) {
    if (!this.isEnabled || !this.isInitialized) return;

    try {
      // Use setTimeout to make analytics non-blocking
      setTimeout(() => {
        try {
          posthog.identify(userId, properties);
        } catch (error) {
          console.warn("PostHog identify failed:", error);
          this.failedAttempts++;
          if (this.failedAttempts >= this.maxFailedAttempts) {
            this.disable();
          }
        }
      }, 0);
    } catch (error) {
      console.warn("PostHog identify setup failed:", error);
      this.failedAttempts++;
      if (this.failedAttempts >= this.maxFailedAttempts) {
        this.disable();
      }
    }
  }

  disable() {
    this.isEnabled = false;
    console.warn("PostHog disabled due to repeated failures");

    // Also disable PostHog features that might cause API calls
    try {
      if (typeof window !== "undefined" && window.posthog) {
        window.posthog.featureFlags = {};
        window.posthog.disable_feature_flags = true;
      }
    } catch (error) {
      console.warn("Error disabling PostHog features:", error);
    }
  }

  reset() {
    this.failedAttempts = 0;
    this.isEnabled = true;
  }

  overridePostHogMethods() {
    try {
      if (typeof window !== "undefined" && window.posthog) {
        // Override feature flag methods to prevent API calls
        window.posthog.getFeatureFlag = () => false;
        window.posthog.isFeatureEnabled = () => false;
        window.posthog.getFeatureFlagPayload = () => null;
        window.posthog.getAllFlags = () => ({});
        window.posthog.reloadFeatureFlags = () => {};

        // Disable feature flag polling
        if (window.posthog.featureFlags) {
          window.posthog.featureFlags = {};
        }

        console.log("PostHog feature flag methods overridden");
      }
    } catch (error) {
      console.warn("Error overriding PostHog methods:", error);
    }
  }
}

// Create singleton instance
const safePostHog = new SafePostHog();

export const initializePostHog = () => {
  safePostHog.init();
  return safePostHog;
};

// Export safe wrapper instead of direct posthog
export default safePostHog;
